import { useState, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Loader2, Check, ArrowLeft, Info, Building2, FileText, Truck, Users } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/site/Navbar';
import Footer from '@/components/site/Footer';
import Seo from '@/components/Seo';
import { apiClient, formatApiErrorDetail } from '@/lib/api';
import { trackEvent } from '@/lib/analytics';

const TIER_OPTIONS = [
  { value: 'boutique',   label: 'Boutique',   range: '1 – 50 kg / week' },
  { value: 'growth',     label: 'Growth',     range: '50 – 500 kg / week' },
  { value: 'enterprise', label: 'Enterprise', range: '500 kg+ / week' },
];

const BUSINESS_TYPES = [
  { v: 'restaurant',    l: 'Restaurant' },
  { v: 'cafe',          l: 'Café' },
  { v: 'supermarket',   l: 'Supermarket' },
  { v: 'butcher',       l: 'Retail butcher' },
  { v: 'distributor',   l: 'Distributor' },
  { v: 'manufacturer',  l: 'Food manufacturer' },
  { v: 'hotel_group',   l: 'Hotel group' },
  { v: 'export',        l: 'Export partner' },
  { v: 'other',         l: 'Other' },
];

const VOLUME_OPTIONS = [
  '1-50kg / week', '50-500kg / week', '500kg+ / week',
];

const TERMS_OPTIONS = [
  { v: 'cod',    l: 'COD',     hint: 'Pay-on-delivery — no credit check needed' },
  { v: '7-day',  l: '7-day',   hint: 'Weekly billing, subject to approval' },
  { v: '14-day', l: '14-day',  hint: 'Standard trade terms, subject to approval' },
  { v: '30-day', l: '30-day',  hint: 'Enterprise terms — references required' },
];

const AM_OPTIONS = [
  { v: 'any',        l: 'Assign anyone available' },
  { v: 'dedicated',  l: 'I want a dedicated manager' },
  { v: 'specific',   l: 'I already know who I want to work with' },
];

const DELIVERY_OPTIONS = [
  { v: 'pickup',   l: 'Malaga pickup' },
  { v: 'metro',    l: 'Perth Metro delivery' },
  { v: 'regional', l: 'Regional WA delivery' },
  { v: 'export',   l: 'Export freight' },
];


export default function WholesaleApply() {
  const [params] = useSearchParams();
  const initialTier = TIER_OPTIONS.find((t) => t.value === params.get('tier'))?.value || 'growth';

  const [form, setForm] = useState({
    business_name: '',
    abn: '',
    business_type: 'restaurant',
    trading_since: '',
    contact_name: '',
    contact_role: '',
    email: '',
    phone: '',
    business_address: '',
    delivery_suburb: '',
    delivery_state: 'WA',
    delivery_postcode: '',
    tier_preference: initialTier,
    estimated_volume: initialTier === 'boutique' ? '1-50kg / week'
                     : initialTier === 'enterprise' ? '500kg+ / week' : '50-500kg / week',
    current_supplier: '',
    trading_terms_preference: 'cod',
    account_manager_preference: 'any',
    account_manager_name: '',
    delivery_preference: 'metro',
    reference_1: '',
    reference_2: '',
    notes: '',
    accepts_terms: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // {id, message}
  const [error, setError] = useState(null);

  const set = (k) => (e) => {
    const v = e && e.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const needsReferences = useMemo(
    () => form.trading_terms_preference !== 'cod',
    [form.trading_terms_preference],
  );

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!form.accepts_terms) {
      setError('You need to accept the trade terms to submit.');
      return;
    }
    if (needsReferences && !form.reference_1.trim()) {
      setError('Please add at least one trade reference for terms accounts.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await apiClient.post('/wholesale/apply', form);
      trackEvent('Wholesale Applied', {
        businessType: form.business_type || 'other',
        volume: form.estimated_volume || 'unknown',
      });
      setSubmitted(data);
      toast.success('Application received — check your inbox for confirmation.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err.message || 'Submission failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="section-light App relative">
        <Navbar />
        <section
          data-testid="wholesale-apply-success"
          className="min-h-[70vh] bg-[#FAFAFA] flex items-center py-24"
        >
          <div className="max-w-2xl mx-auto px-6 sm:px-10 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mx-auto mb-6">
              <Check size={28} className="text-emerald-600" />
            </div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--mw-primary)] mb-3">
              Application received
            </div>
            <h1 className="font-display font-light text-4xl sm:text-5xl leading-tight tracking-tight text-black mb-4">
              You&apos;re in the pipeline.
            </h1>
            <p className="text-black/65 leading-relaxed mb-8">
              {submitted.message} We&apos;ve emailed a confirmation to your inbox with your application ID.
              A member of the trade team will be in touch shortly.
            </p>
            <div className="bg-white border border-black/10 px-6 py-4 mx-auto inline-block">
              <div className="text-[10px] uppercase tracking-[0.24em] text-black/50">Application ID</div>
              <div className="font-display text-lg text-black mt-1 tracking-wider" data-testid="wholesale-apply-id">
                {submitted.id}
              </div>
            </div>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/wholesale"
                className="inline-flex items-center justify-center gap-2 border border-black/25 hover:border-black text-black text-xs uppercase tracking-[0.22em] px-6 py-3 transition-colors"
              >
                <ArrowLeft size={14} /> Back to wholesale
              </Link>
              <Link
                to="/"
                style={{ color: '#fff' }}
                className="inline-flex items-center justify-center gap-2 bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] text-xs uppercase tracking-[0.22em] px-6 py-3 transition-colors"
              >
                Return to homepage
              </Link>
            </div>
          </div>
        </section>
        <Footer />
      </div>
    );
  }

  return (
    <div className="section-light App relative">
      <Seo
        title="Trade Application · Wholesale Onboarding"
        description="Apply for a wholesale trade account with The Meat Works — Boutique, Growth or Enterprise tier. Perth restaurants, cafés, retailers and food manufacturers welcome."
        path="/wholesale/apply"
      />
      <Navbar />

      {/* Header — light editorial */}
      <section className="pt-20 sm:pt-28 pb-10 bg-gradient-to-b from-[#FDFBF7] via-[#F7F3EC] to-[#FAFAFA]">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16 text-center">
          <Link
            to="/wholesale"
            data-testid="wholesale-apply-back"
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-black/60 hover:text-[var(--mw-primary)] transition-colors mb-6"
          >
            <ArrowLeft size={12} /> Back to wholesale
          </Link>
          <div className="text-[10px] uppercase tracking-[0.32em] text-[var(--mw-primary)] mb-3">
            Trade credit application
          </div>
          <h1
            data-testid="wholesale-apply-headline"
            className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tight text-black"
          >
            Open a <em className="not-italic text-[var(--mw-primary)]">trade account</em>.
          </h1>
          <p className="mt-6 text-black/70 max-w-xl mx-auto leading-relaxed">
            5 minutes to submit. Approvals normally within 2 business days. All fields marked with an
            asterisk are required.
          </p>
        </div>
      </section>

      {/* Form */}
      <section className="section-light bg-[#FAFAFA] py-16 sm:py-20 border-t border-black/5">
        <div className="max-w-4xl mx-auto px-6 sm:px-10 lg:px-16">
          <form
            onSubmit={onSubmit}
            data-testid="wholesale-apply-form"
            className="space-y-10"
          >
            {/* 1. Business identity */}
            <Section
              icon={Building2}
              title="1. Business identity"
              hint="ABN, trading name and business type."
            >
              <Row2>
                <TxtField label="Business / trading name *" testId="apply-business-name">
                  <input required minLength={2} maxLength={200} value={form.business_name} onChange={set('business_name')} className="mw-input" />
                </TxtField>
                <TxtField label="ABN * (11-digit — spaces OK)" testId="apply-abn">
                  <input required minLength={9} maxLength={14} placeholder="51 824 753 556" value={form.abn} onChange={set('abn')} className="mw-input" />
                </TxtField>
              </Row2>
              <Row2>
                <TxtField label="Business type *" testId="apply-business-type">
                  <select required value={form.business_type} onChange={set('business_type')} className="mw-input cursor-pointer">
                    {BUSINESS_TYPES.map((b) => (
                      <option key={b.v} value={b.v}>{b.l}</option>
                    ))}
                  </select>
                </TxtField>
                <TxtField label="Trading since (year)" testId="apply-trading-since">
                  <input maxLength={40} placeholder="e.g. 2018" value={form.trading_since} onChange={set('trading_since')} className="mw-input" />
                </TxtField>
              </Row2>
            </Section>

            {/* 2. Contact */}
            <Section icon={Users} title="2. Primary contact" hint="Who should we talk to about this account?">
              <Row2>
                <TxtField label="Contact name *" testId="apply-contact-name">
                  <input required minLength={2} maxLength={200} value={form.contact_name} onChange={set('contact_name')} className="mw-input" />
                </TxtField>
                <TxtField label="Role" testId="apply-contact-role">
                  <input maxLength={100} placeholder="e.g. Head Chef, Purchasing Manager" value={form.contact_role} onChange={set('contact_role')} className="mw-input" />
                </TxtField>
              </Row2>
              <Row2>
                <TxtField label="Email *" testId="apply-email">
                  <input required type="email" value={form.email} onChange={set('email')} className="mw-input" />
                </TxtField>
                <TxtField label="Phone *" testId="apply-phone">
                  <input required minLength={4} maxLength={40} value={form.phone} onChange={set('phone')} className="mw-input" />
                </TxtField>
              </Row2>
            </Section>

            {/* 3. Address */}
            <Section icon={Truck} title="3. Delivery address" hint="Where do you want your first order to land?">
              <TxtField label="Business address *" testId="apply-address">
                <input required minLength={4} maxLength={300} value={form.business_address} onChange={set('business_address')} className="mw-input" />
              </TxtField>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <TxtField label="Suburb" testId="apply-suburb">
                  <input maxLength={120} value={form.delivery_suburb} onChange={set('delivery_suburb')} className="mw-input" />
                </TxtField>
                <TxtField label="State" testId="apply-state">
                  <input maxLength={10} value={form.delivery_state} onChange={set('delivery_state')} className="mw-input uppercase" />
                </TxtField>
                <TxtField label="Postcode" testId="apply-postcode">
                  <input maxLength={10} value={form.delivery_postcode} onChange={set('delivery_postcode')} className="mw-input" />
                </TxtField>
              </div>
              <TxtField label="Preferred delivery" testId="apply-delivery-preference">
                <select value={form.delivery_preference} onChange={set('delivery_preference')} className="mw-input cursor-pointer">
                  {DELIVERY_OPTIONS.map((d) => (
                    <option key={d.v} value={d.v}>{d.l}</option>
                  ))}
                </select>
              </TxtField>
            </Section>

            {/* 4. Trade profile */}
            <Section
              icon={FileText}
              title="4. Trade profile"
              hint="Tier, volume, current supplier — helps us assign the right account manager."
            >
              <TxtField label="Preferred pricing tier *" testId="apply-tier">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {TIER_OPTIONS.map((t) => {
                    const active = form.tier_preference === t.value;
                    return (
                      <button
                        key={t.value}
                        type="button"
                        onClick={() => setForm((f) => ({
                          ...f,
                          tier_preference: t.value,
                          estimated_volume:
                            t.value === 'boutique' ? '1-50kg / week'
                            : t.value === 'enterprise' ? '500kg+ / week'
                            : '50-500kg / week',
                        }))}
                        data-testid={`apply-tier-${t.value}`}
                        className={`text-left p-4 border-2 transition-colors ${
                          active
                            ? 'border-[var(--mw-primary)] bg-white'
                            : 'border-black/10 bg-white hover:border-black/25'
                        }`}
                      >
                        <div className="font-display text-lg text-black">{t.label}</div>
                        <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--mw-primary)] mt-1">
                          {t.range}
                        </div>
                        {active && (
                          <div className="mt-2 flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-emerald-600">
                            <Check size={11} /> Selected
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>
              </TxtField>
              <Row2>
                <TxtField label="Estimated weekly volume *" testId="apply-volume">
                  <select required value={form.estimated_volume} onChange={set('estimated_volume')} className="mw-input cursor-pointer">
                    {VOLUME_OPTIONS.map((v) => (
                      <option key={v} value={v}>{v}</option>
                    ))}
                  </select>
                </TxtField>
                <TxtField label="Current supplier (optional)" testId="apply-current-supplier">
                  <input maxLength={200} placeholder="Who are you buying from today?" value={form.current_supplier} onChange={set('current_supplier')} className="mw-input" />
                </TxtField>
              </Row2>
            </Section>

            {/* 5. Trading terms + account manager */}
            <Section
              icon={Info}
              title="5. Trading terms &amp; account manager"
              hint="Choose your preferred billing rhythm and who runs your account."
            >
              <TxtField label="Preferred trading terms *" testId="apply-terms">
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                  {TERMS_OPTIONS.map((t) => {
                    const active = form.trading_terms_preference === t.v;
                    return (
                      <button
                        key={t.v}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, trading_terms_preference: t.v }))}
                        data-testid={`apply-terms-${t.v}`}
                        className={`text-left p-3 border-2 transition-colors ${
                          active
                            ? 'border-[var(--mw-primary)] bg-white'
                            : 'border-black/10 bg-white hover:border-black/25'
                        }`}
                      >
                        <div className="font-display text-base text-black">{t.l}</div>
                        <div className="text-[10px] text-black/50 uppercase tracking-[0.16em] mt-1 leading-snug">
                          {t.hint}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </TxtField>

              <TxtField label="Account manager assignment *" testId="apply-account-manager">
                <select value={form.account_manager_preference} onChange={set('account_manager_preference')} className="mw-input cursor-pointer">
                  {AM_OPTIONS.map((a) => (
                    <option key={a.v} value={a.v}>{a.l}</option>
                  ))}
                </select>
              </TxtField>

              {form.account_manager_preference === 'specific' && (
                <TxtField label="Account manager name" testId="apply-account-manager-name">
                  <input maxLength={120} placeholder="Name of the person you'd like to work with" value={form.account_manager_name} onChange={set('account_manager_name')} className="mw-input" />
                </TxtField>
              )}

              {needsReferences && (
                <div
                  data-testid="apply-references-block"
                  className="border-l-2 border-[var(--mw-primary)] bg-white px-5 py-4 space-y-4"
                >
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--mw-primary)]">
                    Terms accounts — trade references required
                  </div>
                  <TxtField label="Reference 1 * (business name + contact)" testId="apply-reference-1">
                    <input required={needsReferences} maxLength={300} value={form.reference_1} onChange={set('reference_1')} className="mw-input" placeholder="e.g. ABC Restaurant Group — Sarah Chen · 0400 111 222" />
                  </TxtField>
                  <TxtField label="Reference 2 (optional)" testId="apply-reference-2">
                    <input maxLength={300} value={form.reference_2} onChange={set('reference_2')} className="mw-input" />
                  </TxtField>
                </div>
              )}
            </Section>

            {/* 6. Notes + consent */}
            <Section title="6. Anything else?" hint="Custom cuts, allergens, delivery times — tell us what matters.">
              <TxtField label="Notes" testId="apply-notes">
                <textarea rows={4} maxLength={1500} value={form.notes} onChange={set('notes')} className="mw-input resize-none" />
              </TxtField>

              <label className="flex items-start gap-3 text-sm text-black/75 leading-snug cursor-pointer group">
                <input
                  type="checkbox"
                  checked={form.accepts_terms}
                  onChange={set('accepts_terms')}
                  data-testid="apply-terms-checkbox"
                  className="mt-1 w-4 h-4 accent-[var(--mw-primary)] cursor-pointer"
                />
                <span>
                  I confirm the information above is accurate and I have authority to open a trade account
                  on behalf of the business named. I&apos;ve read The Meat Works&apos; trading terms and
                  agree to the cold-chain, delivery and payment terms tied to my chosen tier.
                </span>
              </label>
            </Section>

            {error && (
              <div
                data-testid="wholesale-apply-error"
                className="border border-red-300 bg-red-50 text-red-800 px-5 py-3 text-sm"
              >
                {error}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3 pt-4">
              <button
                type="submit"
                disabled={submitting}
                data-testid="wholesale-apply-submit"
                style={{ color: '#fff' }}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[var(--mw-primary)] hover:bg-[var(--mw-primary-hover)] disabled:opacity-50 disabled:cursor-not-allowed text-xs uppercase tracking-[0.22em] px-8 py-4 transition-colors"
              >
                {submitting ? <><Loader2 size={14} className="animate-spin" /> Submitting…</> : 'Submit application'}
              </button>
              <Link
                to="/wholesale"
                data-testid="wholesale-apply-cancel"
                className="inline-flex items-center justify-center gap-2 border border-black/25 hover:border-black text-black text-xs uppercase tracking-[0.22em] px-8 py-4 transition-colors"
              >
                Cancel
              </Link>
            </div>
          </form>
        </div>
      </section>

      <Footer />
    </div>
  );
}


function Section({ icon: Icon, title, hint, children }) {
  return (
    <section className="bg-white border border-black/10 p-6 sm:p-8 shadow-[0_8px_30px_-20px_rgba(0,0,0,0.1)]">
      <div className="flex items-start gap-3 mb-6 pb-6 border-b border-black/10">
        {Icon && <Icon size={20} className="text-[var(--mw-primary)] shrink-0 mt-1" />}
        <div>
          <div className="font-display text-xl sm:text-2xl text-black leading-tight">{title}</div>
          {hint && <div className="text-sm text-black/55 mt-1">{hint}</div>}
        </div>
      </div>
      <div className="space-y-5">{children}</div>
    </section>
  );
}


function Row2({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>;
}


function TxtField({ label, testId, children }) {
  return (
    <label className="block" data-testid={testId ? `${testId}-field` : undefined}>
      <div className="text-[10px] uppercase tracking-[0.22em] text-black/55 mb-2">{label}</div>
      {children}
    </label>
  );
}
